/*
  ЦИФРОВОЙ ДИАК ЗФ — FACT GANTT VIEWER

  Встраиваемый просмотр рабочей Google-таблицы (фактический план-график «факт»)
  под планом проекта. Таблица встраивается в безопасный sandbox-iframe.

  Важно про Google: обычная ссылка вида /edit НЕ встраивается (X-Frame-Options).
  Встраиваются:
    • «Все, у кого есть ссылка — Читатель» → URL /preview;
    • «Опубликовать в интернете» → URL /pubhtml (или /pub?...).
  `toEmbeddableSheetUrl` приводит /edit и /d/<id>/... к /preview, pubhtml — как есть.
  Разрешаем ТОЛЬКО docs.google.com (защита от встраивания произвольных сайтов).
*/

import { useState } from "react";
import { ExternalLink, Pencil, Trash2, Table2, AlertTriangle, Check, X } from "lucide-react";
import { cn } from "@/lib/utils";

/** Привести ссылку Google Sheets к встраиваемому виду. null — если не годится. */
export function toEmbeddableSheetUrl(raw: string | undefined | null): string | null {
  const s = (raw ?? "").trim();
  if (!s) return null;
  let u: URL;
  try {
    u = new URL(s);
  } catch {
    return null;
  }
  if (u.protocol !== "https:") return null;
  if (u.hostname !== "docs.google.com") return null; // только Google Sheets
  // Уже опубликованная в интернете таблица — оставляем как есть.
  if (/\/pubhtml/.test(u.pathname) || /\/pub\b/.test(u.pathname)) return u.toString();
  // /spreadsheets/d/<id>/(edit|view|preview...) → /preview, сохраняя gid.
  const m = /\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/.exec(u.pathname);
  if (m) {
    const id = m[1];
    const gidFromHash = /gid=(\d+)/.exec(u.hash || "");
    const gidFromQuery = u.searchParams.get("gid");
    const gid = gidFromHash?.[1] ?? gidFromQuery ?? null;
    return `https://docs.google.com/spreadsheets/d/${id}/preview${gid ? `?gid=${gid}#gid=${gid}` : ""}`;
  }
  // Прочие docs.google.com ссылки — лучше не встраивать.
  return null;
}

interface Props {
  url?: string;
  canEdit: boolean;
  onChange: (url: string | undefined) => void;
}

export function FactGanttViewer({ url, canEdit, onChange }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(url ?? "");

  const embed = toEmbeddableSheetUrl(url);
  const invalidSaved = !!url && !embed;

  const save = () => {
    const next = draft.trim();
    onChange(next ? next : undefined);
    setEditing(false);
  };

  // Форма ввода ссылки (пустое состояние или режим редактирования).
  if (editing || (!url && canEdit)) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted/20 p-4">
        <div className="mb-2 flex items-center gap-2 text-sm font-medium" style={{ color: "var(--nn-dark)" }}>
          <Table2 size={15} className="text-[var(--nn-blue)]" /> Рабочая таблица (факт) — Google Sheets
        </div>
        <input
          type="url"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="https://docs.google.com/spreadsheets/d/…"
          className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-sm"
        />
        <div className="mt-2 rounded-md border border-amber-200 bg-amber-50/60 px-2.5 py-2 text-[11px] text-amber-900">
          <span className="font-semibold">Чтобы таблица отображалась</span>, откройте к ней доступ одним из способов:
          <ul className="ml-4 mt-1 list-disc space-y-0.5">
            <li>«Доступ» → «Все, у кого есть ссылка» → роль «Читатель»; <span className="opacity-80">или</span></li>
            <li>«Файл» → «Доступ» → «Опубликовать в интернете».</li>
          </ul>
          Обычная ссылка на закрытую таблицу во вьювере не откроется (ограничение Google).
        </div>
        <div className="mt-2 flex gap-2">
          <button type="button" onClick={save}
            className="inline-flex items-center gap-1.5 rounded-md bg-[var(--nn-blue)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[var(--nn-blue-dark)]">
            <Check size={14} /> Сохранить
          </button>
          {url || editing ? (
            <button type="button" onClick={() => { setDraft(url ?? ""); setEditing(false); }}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted/50">
              <X size={14} /> Отмена
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  // Ссылки нет и редактировать нельзя — ничего не показываем.
  if (!url) return null;

  return (
    <div className="rounded-lg border border-border bg-card shadow-sm">
      <div className="flex flex-wrap items-center gap-2 border-b border-border/60 px-3 py-2">
        <span className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: "var(--nn-dark)" }}>
          <Table2 size={15} className="text-[var(--nn-blue)]" /> Рабочая таблица (факт)
        </span>
        <div className="ml-auto flex items-center gap-1">
          <a href={url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[12px] text-muted-foreground hover:bg-muted/50 hover:text-[var(--nn-blue)]"
            title="Открыть в Google Sheets в новой вкладке">
            <ExternalLink size={13} /> Открыть
          </a>
          {canEdit ? (
            <>
              <button type="button" onClick={() => { setDraft(url); setEditing(true); }}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[12px] text-muted-foreground hover:bg-muted/50"
                title="Изменить ссылку">
                <Pencil size={13} /> Ссылка
              </button>
              <button type="button"
                onClick={() => { if (window.confirm("Убрать таблицу-факт из проекта? Сама таблица не удалится.")) onChange(undefined); }}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[12px] text-red-700 hover:bg-red-50"
                title="Убрать">
                <Trash2 size={13} />
              </button>
            </>
          ) : null}
        </div>
      </div>

      {embed ? (
        <iframe
          src={embed}
          title="Фактический план-график (Google Sheets)"
          className="block h-[600px] w-full rounded-b-lg border-0"
          loading="lazy"
          referrerPolicy="no-referrer"
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        />
      ) : (
        <div className={cn("flex items-start gap-2 p-4 text-sm", invalidSaved ? "text-amber-900" : "text-muted-foreground")}>
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-500" />
          <div>
            Не удалось встроить эту ссылку. Поддерживаются только ссылки на Google Sheets
            (docs.google.com). Откройте доступ «Все, у кого есть ссылка» или «Опубликовать в интернете»
            и вставьте ссылку заново — кнопка «Ссылка» выше.
            {canEdit ? null : " Обратитесь к редактору проекта."}
          </div>
        </div>
      )}
    </div>
  );
}
